import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { IssueLink } from '@cortex/domain-org-issues';
import { LINK_KINDS } from '@cortex/domain-org-issues/model';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import {
  MultiPicker,
  type MultiPickerOption,
} from '@cortex/web-design-system/components/ui/MultiPicker';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { moveItem, type ActionDraft } from '../../../lib/org-issues.js';

/** @graph-connects none */
interface ActionsEditorProps {
  actions: readonly ActionDraft[];
  candidates: readonly MultiPickerOption[];
  disabled: boolean;
  onChange: (actions: ActionDraft[]) => void;
}

/**
 * Next（誰が・いつまでに・何をするか）の編集。並び順どおりの全件を親に返す
 * （API は全件で置き換える。既存の Next は `id` を持ったまま返すので完了日時が残る）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題の Next を足す・外す・並べ替える・担当と期日と完了を付ける
 * @graph-connects moveItem [calls] 並べ替え
 */
export function ActionsEditor({
  actions,
  candidates,
  disabled,
  onChange,
}: ActionsEditorProps): ReactNode {
  const { t } = useTranslation();
  const update = (index: number, change: Partial<ActionDraft>) => {
    onChange(actions.map((action, i) => (i === index ? { ...action, ...change } : action)));
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
        {t('orgIssues.actions.heading')}
      </h3>
      {actions.length === 0 && (
        <p className="text-sm text-[var(--color-text-muted)]">{t('orgIssues.actions.empty')}</p>
      )}
      {actions.map((action, index) => (
        <div
          key={action.id ?? `new-${String(index)}`}
          className="space-y-2 rounded-md border border-[var(--color-border)] p-2"
        >
          <div className="flex items-center gap-2">
            <Checkbox
              aria-label={t('orgIssues.actions.done')}
              checked={action.done}
              disabled={disabled}
              onChange={(e) => {
                update(index, { done: e.target.checked });
              }}
            />
            <Input
              aria-label={t('orgIssues.actions.text')}
              placeholder={t('orgIssues.actions.text')}
              value={action.text}
              disabled={disabled}
              fullWidth
              onChange={(e) => {
                update(index, { text: e.target.value });
              }}
            />
          </div>
          <div className="grid gap-2 md:grid-cols-[1fr_10rem_auto]">
            <MultiPicker
              label={t('orgIssues.actions.assignees')}
              options={candidates}
              value={action.assigneeIds}
              searchable
              disabled={disabled}
              onChange={(assigneeIds) => {
                update(index, { assigneeIds });
              }}
            />
            <Input
              type="date"
              label={t('orgIssues.actions.dueOn')}
              value={action.dueOn ?? ''}
              disabled={disabled}
              onChange={(e) => {
                update(index, { dueOn: e.target.value === '' ? null : e.target.value });
              }}
            />
            {!disabled && (
              <div className="flex items-end gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onChange(moveItem(actions, index, -1));
                  }}
                >
                  {t('orgIssues.actions.up')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onChange(moveItem(actions, index, 1));
                  }}
                >
                  {t('orgIssues.actions.down')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    onChange(actions.filter((_, i) => i !== index));
                  }}
                >
                  {t('orgIssues.actions.remove')}
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
      {!disabled && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            onChange([...actions, { text: '', assigneeIds: [], dueOn: null, done: false }]);
          }}
        >
          {t('orgIssues.actions.add')}
        </Button>
      )}
    </div>
  );
}

/** @graph-connects none */
interface LinksEditorProps {
  links: readonly IssueLink[];
  disabled: boolean;
  onChange: (links: IssueLink[]) => void;
}

/**
 * 関連リンクの編集。URL の形の検証は API が行う。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題の関連リンク（週次MTG の投稿・改善要望・PR・外部 URL）を足す・外す
 * @graph-connects none
 */
export function LinksEditor({ links, disabled, onChange }: LinksEditorProps): ReactNode {
  const { t } = useTranslation();
  const update = (index: number, change: Partial<IssueLink>) => {
    onChange(links.map((link, i) => (i === index ? { ...link, ...change } : link)));
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
        {t('orgIssues.links.heading')}
      </h3>
      {links.length === 0 && (
        <p className="text-sm text-[var(--color-text-muted)]">{t('orgIssues.links.empty')}</p>
      )}
      {links.map((link, index) =>
        disabled ? (
          <a
            key={`${link.url}-${String(index)}`}
            href={link.url}
            target="_blank"
            rel="noreferrer"
            className="block text-sm text-[var(--color-accent-primary)] underline"
          >
            {link.title} ({t(`orgIssues.links.kinds.${link.kind}`)})
          </a>
        ) : (
          <div
            key={`link-${String(index)}`}
            className="grid gap-2 md:grid-cols-[10rem_1fr_1fr_auto]"
          >
            <Select
              aria-label={t('orgIssues.links.kind')}
              value={link.kind}
              options={LINK_KINDS.map((kind) => ({
                value: kind,
                label: t(`orgIssues.links.kinds.${kind}`),
              }))}
              onChange={(e) => {
                const kind = LINK_KINDS.find((candidate) => candidate === e.target.value);
                if (kind !== undefined) update(index, { kind });
              }}
            />
            <Input
              aria-label={t('orgIssues.links.url')}
              placeholder={t('orgIssues.links.url')}
              value={link.url}
              onChange={(e) => {
                update(index, { url: e.target.value });
              }}
            />
            <Input
              aria-label={t('orgIssues.links.title')}
              placeholder={t('orgIssues.links.title')}
              value={link.title}
              onChange={(e) => {
                update(index, { title: e.target.value });
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                onChange(links.filter((_, i) => i !== index));
              }}
            >
              {t('orgIssues.links.remove')}
            </Button>
          </div>
        ),
      )}
      {!disabled && (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => {
            onChange([...links, { kind: 'url', url: '', title: '' }]);
          }}
        >
          {t('orgIssues.links.add')}
        </Button>
      )}
    </div>
  );
}
